import React from "react";

export type Card = { id: string; name: string; arcana: string; numeral: string; suit?: string | null; keywords: string };

const SUIT_GLYPH: Record<string, string> = { Wands: "|", Cups: "U", Swords: "†", Pentacles: "◎" };

export function TarotCard({ card, reversed, faceUp = true, size = "md", onClick, className = "", draggable, onDragStart }: {
  card?: Card | null; reversed?: boolean; faceUp?: boolean; size?: "sm" | "md" | "lg"; onClick?: () => void; className?: string;
  draggable?: boolean; onDragStart?: (e: React.DragEvent) => void;
}) {
  const dims = size === "sm" ? "w-14 h-[88px]" : size === "lg" ? "w-32 h-52" : "w-20 h-32";
  if (!card || !faceUp) {
    return <div onClick={onClick} draggable={draggable} onDragStart={onDragStart}
      className={`${dims} rounded-md tarot-back cursor-pointer select-none transition-transform hover:-translate-y-1 ${className}`} />;
  }
  const major = card.arcana === "major";
  return (
    <div onClick={onClick} draggable={draggable} onDragStart={onDragStart}
      className={`${dims} card-flip rounded-md bg-[#F6F4EE] text-[#121110] border border-[#C5A059] p-1.5 flex flex-col justify-between select-none cursor-pointer shadow-[0_6px_18px_-8px_rgba(0,0,0,0.6)] transition-transform hover:-translate-y-1 ${className}`}
      style={{ transform: reversed ? "rotate(180deg)" : undefined }}>
      <div className="flex items-start justify-between">
        <span className="mono text-[9px] leading-none">{card.numeral}</span>
        {card.suit && <span className="mono text-[9px] leading-none">{SUIT_GLYPH[card.suit]}</span>}
      </div>
      <div className="flex-1 flex items-center justify-center">
        <span className={`serif text-center leading-tight ${size === "sm" ? "text-[8px]" : size === "lg" ? "text-base" : "text-[11px]"} ${major ? "font-semibold" : ""}`}>{card.name}</span>
      </div>
      <div className="flex items-end justify-between">
        <span className={`h-px flex-1 ${major ? "bg-[#581C25]" : "bg-[#C5A059]"}`} />
      </div>
    </div>
  );
}

export function SnapshotView({ snap, deck, compact }: { snap: any; deck?: Card[]; compact?: boolean }) {
  if (!snap) return null;
  const find = (id: string) => deck?.find((c) => c.id === id) || { id, name: id, arcana: "minor", numeral: "", keywords: "" };
  return (
    <div className="rounded-md table-surface border border-[#2A2825] p-4 text-[#F6F4EE]" data-testid="snapshot-view">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="eyebrow !text-[#C5A059]">{snap.spread_name}</p>
          <p className="serif text-lg">{snap.title}</p>
        </div>
        <span className="mono text-[10px] text-[#A8A59C]">{new Date(snap.created_at).toLocaleDateString()}</span>
      </div>
      <div className="flex flex-wrap gap-3">
        {snap.slots.map((s: any) => (
          <div key={s.position} className="flex flex-col items-center gap-1 w-16">
            <TarotCard card={s.card_id ? (find(s.card_id) as Card) : null} reversed={s.reversed} faceUp={!!s.card_id} size="sm" />
            <span className="mono text-[8px] uppercase tracking-wider text-[#A8A59C] text-center leading-tight">{s.label}{s.reversed ? " · R" : ""}</span>
          </div>
        ))}
      </div>
      {!compact && snap.notes && <p className="mt-4 text-sm text-[#A8A59C] whitespace-pre-wrap">{snap.notes}</p>}
    </div>
  );
}
